import { useEffect, useState, type ReactNode } from 'react';
import { resolveRoleFromRealmRoles, type Profile, type User } from '@erp/shared';
import { isKeycloakMode } from '@/config/env';
import { api, setAccessEndedHandler, setTokenProvider, setUnauthorizedHandler } from '@/lib/api-client';
import { accessEndedActions } from '@/lib/access-ended';
import { Spinner } from '@/components/ui/misc';
import { setSessionUser, updateStoredUser } from './use-auth';
import { performLogout } from './logout';
import {
  clearSelectedTenant,
  getSelectedTenant,
  setKnownTenants,
  setSelectedTenant,
  tenantLabel,
  tenantsFromErrorDetails,
  type TenantOption,
} from '@/lib/tenant-selection';
import {
  ensureKeycloakInitialized,
  getKeycloak,
  resetLoginInFlight,
  triggerKeycloakLogin,
} from './keycloak';
import { useImpersonationState } from './impersonation';
import { ImpersonationEnded } from '@/features/impersonation/ImpersonationEnded';
import { ImpersonationGate } from '@/features/impersonation/ImpersonationGate';

type GateState = 'loading' | 'ready' | 'error' | 'choose-tenant' | 'access-ended';

/**
 * BYPASS das rotas públicas: /kb, /kb/:slug (base de conhecimento) e /chat
 * (widget de atendimento) são rotas anônimas (fora do AppLayout, servidas por
 * /api/v1/public/*). Em modo keycloak o AuthGate embrulha o app inteiro e
 * forçaria login do visitante — nessas rotas renderizamos os children direto,
 * SEM inicializar o keycloak-js. Avaliado no load da página; navegar de uma
 * rota pública para uma interna exige full page load (as páginas públicas não
 * linkam para dentro do app).
 *
 * Regex: casa exatamente /kb, /chat, /rate (avaliação CSAT por link de
 * e-mail — v4) ou /form (formulário externo de abertura — v5), ou qualquer
 * subrota (/kb/..., /rate/:token..., /form/:token). O grupo final (\/|$)
 * impede casar prefixos de rotas internas — /chats NÃO casa (após "chat" vem
 * "s", não "/" nem fim da string).
 */
const PUBLIC_PATH_RE = /^\/(kb|chat|rate|form)(\/|$)/;

function isPublicPath(): boolean {
  return PUBLIC_PATH_RE.test(window.location.pathname);
}

/** Usuário provisório derivado do token (id 0 → GET /me traz o id local real). */
function userFromToken(): User {
  const parsed = getKeycloak().tokenParsed;
  const roles: string[] = parsed?.realm_access?.roles ?? [];
  return {
    id: 0,
    name:
      (parsed?.name as string | undefined) ??
      (parsed?.preferred_username as string | undefined) ??
      '',
    email: (parsed?.email as string | undefined) ?? '',
    role: resolveRoleFromRealmRoles(roles),
    active: true,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Porta de entrada do SPA. A aba de depuração "ver como cliente" (spec 020)
 * tem porta própria: nunca inicializa o Keycloak nem usa a sessão do admin —
 * só o token de depuração da aba, com a faixa em toda tela, e termina na tela
 * de sessão encerrada.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const impersonation = useImpersonationState();
  if (impersonation.mode === 'ended') return <ImpersonationEnded reason={impersonation.reason} />;
  if (impersonation.mode === 'active') return <ImpersonationGate>{children}</ImpersonationGate>;
  return <SessionAuthGate>{children}</SessionAuthGate>;
}

/**
 * Modo keycloak: inicializa o keycloak-js ANTES de renderizar o router
 * (splash enquanto isso); sem sessão SSO → redirect pra página de login
 * temada do Keycloak (não há UI de login local). Autenticado: registra o
 * provider de token + handler de 401 no api-client e persiste o usuário.
 *
 * Modo local: passthrough — comportamento atual intacto.
 */
function SessionAuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(
    isKeycloakMode && !isPublicPath() ? 'loading' : 'ready'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);

  // Spec 042: o 401 `membership_inactive` — o acesso a esta empresa foi
  // encerrado. Nos DOIS modos, e registrado ANTES do boot abaixo (efeitos rodam
  // na ordem): o /me do boot já pode voltar com ele.
  useEffect(() => {
    setAccessEndedHandler(() => setState('access-ended'));
    return () => setAccessEndedHandler(null);
  }, []);

  useEffect(() => {
    // Rotas públicas (/kb*, /chat): bypass total — sem init do Keycloak.
    if (!isKeycloakMode || isPublicPath()) return;

    let cancelled = false;

    (async () => {
      try {
        const authenticated = await ensureKeycloakInitialized();
        if (cancelled) return;

        if (!authenticated) {
          // Sem sessão SSO → login no Keycloak (mantém o splash até o redirect).
          triggerKeycloakLogin();
          return;
        }

        const keycloak = getKeycloak();
        // Bearer passa a vir do keycloak-js (token em memória, renovado pelo
        // onTokenExpired); 401 → reautentica no Keycloak, não no /login local.
        setTokenProvider(() => keycloak.token ?? null);
        setUnauthorizedHandler(() => triggerKeycloakLogin());

        setSessionUser(userFromToken());

        // Perfil local ANTES de liberar o router: id real + nome/e-mail
        // possivelmente editados no ERP, e sobretudo o PAPEL definitivo (o do
        // banco é a autoridade; o do token é derivação).
        //
        // A ordem é o ponto. O `beforeLoad` da rota autenticada lê o usuário
        // UMA vez, na primeira avaliação, e é ele que manda o papel `client`
        // para /portal. Liberando o router antes desta resposta, ele decide com
        // o usuário provisório e monta o app da equipe; quando o papel real
        // chega, `beforeLoad` já rodou e só volta a rodar na próxima navegação
        // — daí o sintoma de "só vai para o portal depois que eu clico em algo,
        // e recarregar traz a tela errada de novo".
        //
        // O try/catch é PRÓPRIO de propósito: agora que esta chamada bloqueia o
        // splash, deixá-la no catch de fora transformaria qualquer soluço de
        // rede em app travado na tela de erro. Falhando, seguimos com o
        // provisório do token — que já traz o papel certo por
        // resolveRoleFromRealmRoles — e o app abre normalmente.
        try {
          const res = await api<Profile>('/me');
          if (cancelled) return;
          // Mais de uma empresa e nenhuma escolhida: a API não adivinha (seria
          // escrever na empresa errada) e devolve a lista — a escolha é aqui.
          if (res.error?.error === 'tenant_selection_required') {
            const options = tenantsFromErrorDetails(res.error.details);
            setKnownTenants(options);
            setTenantOptions(options);
            setState('choose-tenant');
            return;
          }
          // Escolha antiga que o token não permite mais (saiu da empresa):
          // esquece e pergunta de novo, em vez de 403 em toda tela.
          if (res.error?.error === 'tenant_not_allowed' && getSelectedTenant()) {
            clearSelectedTenant();
            window.location.reload();
            return;
          }
          if (res.data) {
            const { id, name, email, role } = res.data;
            updateStoredUser({ id, name, email, role });
          }
        } catch {
          if (cancelled) return;
        }
        setState((current) => (current === 'access-ended' ? current : 'ready'));
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(e instanceof Error ? e.message : 'Falha ao inicializar o Keycloak');
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      // Remount (StrictMode/HMR) não pode ficar travado sem poder relogar.
      resetLoginInFlight();
    };
  }, []);

  if (state === 'access-ended') return <AccessEnded />;

  if (state === 'choose-tenant') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
          <h1 className="text-base font-semibold text-foreground">Escolha a empresa</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Seu acesso vale para mais de uma empresa. Você pode trocar depois, no menu do seu
            usuário.
          </p>
          <ul className="mt-4 space-y-2">
            {tenantOptions.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="w-full rounded-md border px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary"
                  onClick={() => {
                    setSelectedTenant(option.slug);
                    // recarregar zera cache de consultas e o stream da empresa anterior
                    window.location.reload();
                  }}
                >
                  {tenantLabel(option.slug)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4">
        <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
          <p className="text-sm text-muted-foreground">
            Não foi possível inicializar a autenticação.
            {errorMessage ? ` (${errorMessage})` : ''}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner className="size-8" />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Spec 042: quem administra a empresa no ERP desativou o acesso desta pessoa a
 * ela. Não é sessão vencida — entrar de novo daria no mesmo —, então a tela não
 * oferece "entrar": oferece outra empresa (para quem tem mais de uma) e sair.
 */
function AccessEnded() {
  const actions = accessEndedActions(getSelectedTenant());
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
        <h1 className="text-base font-semibold text-foreground">
          Seu acesso a esta empresa foi encerrado
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Quem administra a empresa no ERP desativou o seu acesso. Se foi engano, fale com essa
          pessoa.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {actions.includes('switch') ? (
            <button
              type="button"
              className="w-full rounded-md border px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => {
                clearSelectedTenant();
                window.location.reload();
              }}
            >
              Entrar em outra empresa
            </button>
          ) : null}
          <button
            type="button"
            className="w-full rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
            onClick={() =>
              performLogout(() => {
                window.location.href = '/login';
              })
            }
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
